/**
 * What a ticket design is (D-89).
 *
 * A design is a canvas and a list of elements placed on it, in **mils**
 * (thousandths of an inch) from the top-left corner. Mils rather than points
 * or pixels because ticket stock is sold in inches, BOCA counts in dots at 200
 * or 300 dpi, and a PDF counts in points: one physical unit converts cleanly
 * into all three, and a design moved between them keeps its real size.
 *
 * Every element is data. There is no expression language, no script, no style
 * string and no HTML: a text element has a font from a fixed list and a size
 * in points; a field element names a merge field from the registry; a
 * condition is a comparison between a named field and a literal. A renderer
 * therefore never evaluates anything the designer wrote — it looks values up
 * and draws.
 */

export type CanvasKind = 'PDF' | 'BOCA';

export interface Margins {
  top_mils: number;
  right_mils: number;
  bottom_mils: number;
  left_mils: number;
}

export interface TicketCanvas {
  kind: CanvasKind;
  /** The physical stock, as it feeds: width across, height down. */
  width_mils: number;
  height_mils: number;
  /** Where a printer physically cannot print, or where a stock is perforated. */
  margins: Margins;
  /** BOCA only: the printer's resolution, which decides the smallest readable barcode. */
  dpi?: number;
  /** A tear-off stub down the right, in mils from the right edge. Zero means none. */
  stub_width_mils?: number;
  background_hex?: string | null;
}

export type ElementKind = 'TEXT' | 'FIELD' | 'IMAGE' | 'SPONSOR' | 'QR' | 'BARCODE' | 'BOX' | 'LINE';

export type FontFamily = 'SANS' | 'SERIF' | 'MONO';
export type FontWeight = 'NORMAL' | 'BOLD';
export type TextAlign = 'LEFT' | 'CENTER' | 'RIGHT';

/** A condition is a comparison, or a combination of them. Never code (PART 13). */
export type Condition =
  | { field: string; op: 'EXISTS' | 'NOT_EXISTS' }
  | { field: string; op: 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS'; value: string }
  | { field: string; op: 'IN' | 'NOT_IN'; values: string[] }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

export interface ElementBase {
  id: string;
  kind: ElementKind;
  /** From the top-left of the stock, including anything in the margins. */
  x_mils: number;
  y_mils: number;
  width_mils: number;
  height_mils: number;
  rotation?: 0 | 90 | 180 | 270;
  /** Drawn only when this holds. Absent means always. */
  visible_when?: Condition | null;
  /** What the designer calls it in the element list. */
  name?: string | null;
  locked?: boolean;
}

export interface TextStyle {
  font: FontFamily;
  size_pt: number;
  weight: FontWeight;
  align: TextAlign;
  /** PDF only; a BOCA prints black on white however this is set. */
  color_hex?: string | null;
  line_height?: number;
  uppercase?: boolean;
  /** Shrink to fit rather than overflow, down to this size. */
  min_size_pt?: number | null;
  max_lines?: number | null;
}

export interface TextElement extends ElementBase, TextStyle {
  kind: 'TEXT';
  text: string;
}

export interface FieldElement extends ElementBase, TextStyle {
  kind: 'FIELD';
  /** A key from the merge-field registry — nothing else is accepted. */
  field: string;
  prefix?: string | null;
  suffix?: string | null;
  /** What to print when the field is empty. Empty string prints nothing. */
  fallback?: string | null;
}

export interface ImageElement extends ElementBase {
  kind: 'IMAGE';
  /** A media-library id. There is no URL in a design. */
  file_id: string;
  fit?: 'CONTAIN' | 'COVER';
}

export interface SponsorElement extends ElementBase {
  kind: 'SPONSOR';
  /** A specific sponsor, or whoever is presenting this event. */
  sponsor_id?: string | null;
  source: 'EXPLICIT' | 'EVENT_PRESENTING' | 'EVENT_FIRST';
  variant: 'COLOR' | 'REVERSED' | 'BLACK' | 'WHITE';
}

export interface QrElement extends ElementBase {
  kind: 'QR';
  /** What the code carries. A credential is the admission; a URL is not. */
  source: 'CREDENTIAL' | 'SHORT_CODE' | 'ORDER_URL';
  error_correction?: 'L' | 'M' | 'Q' | 'H';
  /** Blank space that must surround the code, in mils. */
  quiet_zone_mils?: number;
}

export interface BarcodeElement extends ElementBase {
  kind: 'BARCODE';
  symbology: 'CODE128' | 'CODE39';
  source: 'SHORT_CODE' | 'ORDER_NUMBER';
  human_readable?: boolean;
  quiet_zone_mils?: number;
}

export interface BoxElement extends ElementBase {
  kind: 'BOX';
  fill_hex?: string | null;
  border_hex?: string | null;
  border_width_mils?: number;
  corner_radius_mils?: number;
}

export interface LineElement extends ElementBase {
  kind: 'LINE';
  thickness_mils?: number;
  color_hex?: string | null;
  dashed?: boolean;
}

export type DesignElement = TextElement | FieldElement | ImageElement | SponsorElement | QrElement | BarcodeElement | BoxElement | LineElement;

export interface TicketDesign {
  /** Bumped when this file's shape changes, so a stored design can be read forever. */
  schema_version: 1;
  canvas: TicketCanvas;
  elements: DesignElement[];
}

export const DEFAULT_MARGINS: Margins = { top_mils: 100, right_mils: 100, bottom_mils: 100, left_mils: 100 };

/** US Letter, for a PDF ticket at home. */
export const LETTER_CANVAS: TicketCanvas = { kind: 'PDF', width_mils: 8500, height_mils: 11000, margins: { top_mils: 500, right_mils: 500, bottom_mils: 500, left_mils: 500 } };

/** The common BOCA stock: 2" × 5.5", printed landscape. */
export const BOCA_CANVAS: TicketCanvas = { kind: 'BOCA', width_mils: 5500, height_mils: 2000, margins: DEFAULT_MARGINS, dpi: 300, stub_width_mils: 1500 };

// --- Reading a design safely ----------------------------------------------------------------------

const FONTS = new Set<FontFamily>(['SANS', 'SERIF', 'MONO']);
const WEIGHTS = new Set<FontWeight>(['NORMAL', 'BOLD']);
const ALIGNS = new Set<TextAlign>(['LEFT', 'CENTER', 'RIGHT']);
const ROTATIONS = new Set([0, 90, 180, 270]);
const KINDS = new Set<ElementKind>(['TEXT', 'FIELD', 'IMAGE', 'SPONSOR', 'QR', 'BARCODE', 'BOX', 'LINE']);

const num = (value: unknown, fallback: number, min = -100000, max = 100000): number => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(Math.max(n, min), max) : fallback;
};
const text = (value: unknown, max = 500): string => (typeof value === 'string' ? value.slice(0, max) : '');
const hex = (value: unknown): string | null => (typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : null);

const OPS = new Set(['EXISTS', 'NOT_EXISTS', 'EQUALS', 'NOT_EQUALS', 'CONTAINS', 'IN', 'NOT_IN']);

/** A condition, or nothing. An unreadable condition is dropped, never guessed at. */
export function parseCondition(input: unknown, depth = 0): Condition | null {
  if (!input || typeof input !== 'object' || depth > 4) return null;
  const value = input as Record<string, unknown>;
  if (Array.isArray(value['all'])) {
    const all = value['all'].map((child) => parseCondition(child, depth + 1)).filter((child): child is Condition => child !== null);
    return all.length > 0 ? { all } : null;
  }
  if (Array.isArray(value['any'])) {
    const any = value['any'].map((child) => parseCondition(child, depth + 1)).filter((child): child is Condition => child !== null);
    return any.length > 0 ? { any } : null;
  }
  if (value['not']) {
    const not = parseCondition(value['not'], depth + 1);
    return not ? { not } : null;
  }
  const field = typeof value['field'] === 'string' ? value['field'] : null;
  const op = typeof value['op'] === 'string' ? value['op'] : null;
  if (!field || !op || !OPS.has(op)) return null;
  if (op === 'EXISTS' || op === 'NOT_EXISTS') return { field, op };
  if (op === 'IN' || op === 'NOT_IN') {
    const values = Array.isArray(value['values']) ? value['values'].filter((v): v is string => typeof v === 'string').slice(0, 50) : [];
    return values.length > 0 ? { field, op, values } : null;
  }
  return { field, op: op as 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS', value: text(value['value'], 200) };
}

function textStyle(value: Record<string, unknown>): TextStyle {
  const font = value['font'] as FontFamily;
  const weight = value['weight'] as FontWeight;
  const align = value['align'] as TextAlign;
  return {
    font: FONTS.has(font) ? font : 'SANS',
    size_pt: num(value['size_pt'], 10, 4, 96),
    weight: WEIGHTS.has(weight) ? weight : 'NORMAL',
    align: ALIGNS.has(align) ? align : 'LEFT',
    color_hex: hex(value['color_hex']),
    line_height: num(value['line_height'], 1.2, 0.8, 3),
    uppercase: value['uppercase'] === true,
    min_size_pt: value['min_size_pt'] === null || value['min_size_pt'] === undefined ? null : num(value['min_size_pt'], 6, 4, 96),
    max_lines: value['max_lines'] === null || value['max_lines'] === undefined ? null : Math.round(num(value['max_lines'], 1, 1, 20)),
  };
}

/**
 * Reads any stored or posted design into the shape above.
 *
 * Applied when a design is saved and again when it is rendered, so an old
 * version, a hand-edited JSON blob or a bug all resolve to something a
 * renderer can draw without asking questions.
 */
export function parseDesign(input: unknown): TicketDesign {
  const raw = (input ?? {}) as Record<string, unknown>;
  const canvasRaw = (raw['canvas'] ?? {}) as Record<string, unknown>;
  const kind: CanvasKind = canvasRaw['kind'] === 'BOCA' ? 'BOCA' : 'PDF';
  const base = kind === 'BOCA' ? BOCA_CANVAS : LETTER_CANVAS;
  const marginsRaw = (canvasRaw['margins'] ?? {}) as Record<string, unknown>;
  const canvas: TicketCanvas = {
    kind,
    width_mils: num(canvasRaw['width_mils'], base.width_mils, 500, 30000),
    height_mils: num(canvasRaw['height_mils'], base.height_mils, 500, 30000),
    margins: {
      top_mils: num(marginsRaw['top_mils'], base.margins.top_mils, 0, 5000),
      right_mils: num(marginsRaw['right_mils'], base.margins.right_mils, 0, 5000),
      bottom_mils: num(marginsRaw['bottom_mils'], base.margins.bottom_mils, 0, 5000),
      left_mils: num(marginsRaw['left_mils'], base.margins.left_mils, 0, 5000),
    },
    ...(kind === 'BOCA' ? { dpi: canvasRaw['dpi'] === 200 ? 200 : 300 } : {}),
    stub_width_mils: num(canvasRaw['stub_width_mils'], kind === 'BOCA' ? (base.stub_width_mils ?? 0) : 0, 0, 10000),
    background_hex: hex(canvasRaw['background_hex']),
  };

  const elements: DesignElement[] = [];
  const seen = new Set<string>();
  for (const raw2 of Array.isArray(raw['elements']) ? (raw['elements'] as unknown[]) : []) {
    const element = parseElement(raw2, seen);
    if (element) elements.push(element);
  }
  return { schema_version: 1, canvas, elements };
}

function parseElement(input: unknown, seen: Set<string>): DesignElement | null {
  if (!input || typeof input !== 'object') return null;
  const value = input as Record<string, unknown>;
  const kind = value['kind'] as ElementKind;
  if (!KINDS.has(kind)) return null;
  const id = text(value['id'], 64) || `el-${seen.size + 1}`;
  if (seen.has(id)) return null;
  seen.add(id);

  const rotation = Number(value['rotation'] ?? 0);
  const base: ElementBase = {
    id,
    kind,
    x_mils: Math.round(num(value['x_mils'], 0, -5000, 30000)),
    y_mils: Math.round(num(value['y_mils'], 0, -5000, 30000)),
    width_mils: Math.round(num(value['width_mils'], 1000, 1, 30000)),
    height_mils: Math.round(num(value['height_mils'], 200, 1, 30000)),
    rotation: (ROTATIONS.has(rotation) ? rotation : 0) as 0 | 90 | 180 | 270,
    visible_when: parseCondition(value['visible_when']),
    name: text(value['name'], 80) || null,
    locked: value['locked'] === true,
  };

  switch (kind) {
    case 'TEXT':
      return { ...base, kind, ...textStyle(value), text: text(value['text'], 2000) };
    case 'FIELD':
      return {
        ...base,
        kind,
        ...textStyle(value),
        field: text(value['field'], 80),
        prefix: text(value['prefix'], 60) || null,
        suffix: text(value['suffix'], 60) || null,
        fallback: typeof value['fallback'] === 'string' ? text(value['fallback'], 120) : null,
      };
    case 'IMAGE':
      return { ...base, kind, file_id: text(value['file_id'], 64), fit: value['fit'] === 'COVER' ? 'COVER' : 'CONTAIN' };
    case 'SPONSOR':
      return {
        ...base,
        kind,
        source: value['source'] === 'EXPLICIT' ? 'EXPLICIT' : value['source'] === 'EVENT_FIRST' ? 'EVENT_FIRST' : 'EVENT_PRESENTING',
        sponsor_id: text(value['sponsor_id'], 64) || null,
        variant: ['COLOR', 'REVERSED', 'BLACK', 'WHITE'].includes(String(value['variant'])) ? (value['variant'] as SponsorElement['variant']) : 'BLACK',
      };
    case 'QR':
      return {
        ...base,
        kind,
        source: value['source'] === 'SHORT_CODE' ? 'SHORT_CODE' : value['source'] === 'ORDER_URL' ? 'ORDER_URL' : 'CREDENTIAL',
        error_correction: (['L', 'M', 'Q', 'H'].includes(String(value['error_correction'])) ? value['error_correction'] : 'M') as 'L' | 'M' | 'Q' | 'H',
        quiet_zone_mils: Math.round(num(value['quiet_zone_mils'], 40, 0, 500)),
      };
    case 'BARCODE':
      return {
        ...base,
        kind,
        symbology: value['symbology'] === 'CODE39' ? 'CODE39' : 'CODE128',
        source: value['source'] === 'ORDER_NUMBER' ? 'ORDER_NUMBER' : 'SHORT_CODE',
        human_readable: value['human_readable'] !== false,
        quiet_zone_mils: Math.round(num(value['quiet_zone_mils'], 100, 0, 500)),
      };
    case 'BOX':
      return {
        ...base,
        kind,
        fill_hex: hex(value['fill_hex']),
        border_hex: hex(value['border_hex']),
        border_width_mils: Math.round(num(value['border_width_mils'], 10, 0, 200)),
        corner_radius_mils: Math.round(num(value['corner_radius_mils'], 0, 0, 500)),
      };
    case 'LINE':
      return { ...base, kind, thickness_mils: Math.round(num(value['thickness_mils'], 10, 1, 200)), color_hex: hex(value['color_hex']), dashed: value['dashed'] === true };
    default:
      return null;
  }
}
